/* eslint-disable import-x/no-namespace */
import { useMemo } from 'react';

import type { PointGetCardsFilters } from '@vlabs/api/types';
import { Control } from '@vlabs/uikit';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';

import { filterIsApplied, FilterMenu } from '@vlabs/monokit/shared/ui/filter-menu';
import { withPopover } from '@vlabs/monokit/shared/ui/popovers/withPopover';
import { SideMenuFilterItem } from '@vlabs/monokit/shared/ui/side-menu-filter-item';

import { ListsFilterPopover } from './popovers/ListsFilterPopoverPoint3';
import * as sideMenuFilters from './side-menu';
import { useCardsFilters } from './useCardsFilters';

const withLeftPopover = withPopover({ placement: 'left' });
const withSideMenuItemLeftPopover = withLeftPopover(SideMenuFilterItem);
const ListsFilterWithLeftPopover = withSideMenuItemLeftPopover(ListsFilterPopover);

interface CardListFilterMenuProps {
  exclude?: (keyof PointGetCardsFilters)[];
}

function CardListFilterMenuPoint3({
  exclude = undefined,
}: CardListFilterMenuProps): JSX.Element {
  const { t } = useTranslation();

  const { filters, setFilters } = useCardsFilters();
  const isResetDisabled = Object.values(omit(filters, 'page', 'page_size', ...(exclude ?? []))).filter((v) => v !== undefined).length === 0;

  const $filters = useMemo(() => {
    const result = [];
    if (!exclude?.includes('card_ids')) {
      result.push(<sideMenuFilters.ExternalIdFilterWithLeftPopover
        badge={filterIsApplied(!!filters.card_ids)}
        key="card_ids"
        testId="card_ids-Filter"
        value={t('card-list-page:filters.popovers.external id.label')}
      />);
    }
    if (!exclude?.includes('user_data.fio__ilike')) {
      result.push(<sideMenuFilters.FioFilterWithLeftPopover
        badge={filterIsApplied(!!filters['user_data.fio__ilike'])}
        key="user_data"
        testId="user_data-Filter"
        value={t('card-list-page:filters.popovers.fio.label')}
      />);
    }
    if (!exclude?.includes('create_time__gte') && !exclude?.includes('create_time__lt')) {
      result.push(<sideMenuFilters.DateFilterWithLeftPopover
        badge={filterIsApplied(!!(filters.create_time__lt ?? filters.create_time__gte))}
        key="create_time"
        testId="create_time-Filter"
        value={t('card-list-page:filters.popovers.date.label')}
      />);
    }
    if (!exclude?.includes('list_ids')) {
      result.push(<ListsFilterWithLeftPopover
        badge={filterIsApplied(!!filters.list_ids)}
        key="list_ids"
        testId="list_ids-Filter"
        value={t('card-list-page:filters.popovers.lists.label')}
      />);
    }
    return result;
  }, [exclude, filters, t]);

  const excludedFilters = (Object.keys(filters) as (keyof PointGetCardsFilters)[])
    .filter((filterKey) => exclude?.includes(filterKey))
    .reduce<PointGetCardsFilters>((prev, current) => ({
      ...prev,
      [current]: filters[current],
    }), {}) ?? {};

  return (
    <FilterMenu
      filters={$filters}
      footer={(
        <Control.Button
          data-testid="button.reset-cards-filters"
          disabled={isResetDisabled}
          fullWidth
          kind="negative"
          onClick={() => setFilters({
            ...excludedFilters,
            page: 0,
            page_size: 25,
          })}
          type="button"
        >
          {t('card-list-page:filters.buttons.reset button.label')}
        </Control.Button>
      )}
    />
  );
}

export { CardListFilterMenuPoint3 };
